import { useEffect, useMemo, useState } from 'react';
import {
  listarApuracoesDAS, apurarDAS, aprovarDAS, refAnexosDAS, abrirDocumentoAutenticado } from '../api';
import { PageHeader, Loading, Empty, StatusBadge, Alert, KPI } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, anosOpcoes, mesOpcoes } from '../lib/format';

export default function ApuracaoDAS({ clienteId }) {
  const comp = mesAtual();
  const [ano, mes] = comp.split('-').map(Number);

  const [filtroAno, setFiltroAno] = useState(ano);
  const [apuracoes, setApuracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState(null);
  const [formAberto, setFormAberto] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [anexos, setAnexos] = useState([]);

  // Form de nova apuração
  const [formComp, setFormComp] = useState(`${ano}-${String(mes).padStart(2, '0')}`);
  const [formReceitaMes, setFormReceitaMes] = useState('');
  const [formRbt12, setFormRbt12] = useState('');
  const [formAnexoForcado, setFormAnexoForcado] = useState('auto');
  const [formAtividadeIV, setFormAtividadeIV] = useState(false);
  const [apurando, setApurando] = useState(false);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    listarApuracoesDAS(clienteId, { ano: filtroAno })
      .then(r => setApuracoes(r.items || []))
      .catch(err => setMensagem({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId, filtroAno]);
  useEffect(() => {
    refAnexosDAS().then(r => setAnexos(r.items || [])).catch(() => {});
  }, []);

  const apurar = async () => {
    setApurando(true);
    setMensagem(null);
    try {
      const dados = {
        competencia: formComp,
        anexo_forcado: formAnexoForcado,
        atividade_anexo_iv: formAtividadeIV,
      };
      if (formReceitaMes) dados.receita_mes = Number(formReceitaMes);
      if (formRbt12) dados.rbt12 = Number(formRbt12);
      const r = await apurarDAS(clienteId, dados);
      setMensagem({
        tipo: 'success',
        txt: `Apuração ${formComp} criada · DAS R$ ${r.apuracao.valor_das}`
          + (r.receita ? ` · Receita ${fmtMoeda(r.receita.valor)} (${r.receita.origem})` : '')
          + (r.rbt12 ? ` · RBT12 ${r.rbt12.origem}` : '')
          + (r.fatorR ? ` · Fator R ${(r.fatorR.valor * 100).toFixed(2)}% (${r.fatorR.origem})` : ''),
      });
      setFormReceitaMes(''); setFormRbt12('');
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    } finally {
      setApurando(false);
    }
  };

  const aprovar = async (id) => {
    try {
      await aprovarDAS(id);
      setMensagem({ tipo: 'success', txt: `Apuração #${id} aprovada — liberada pra DARF` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  // KPIs
  const totalAno = apuracoes.reduce((s, a) => s + Number(a.valor_das || 0), 0);
  const aprovadas = apuracoes.filter(a => a.status === 'aprovado' || a.status === 'pago').length;
  const rascunhos = apuracoes.filter(a => a.status === 'rascunho').length;
  const ultima = apuracoes[0];

  // Memoria de calculo imprimivel: rota autenticada, aba aberta no gesto.
  const imprimir = (a) =>
    abrirDocumentoAutenticado(
      `/apuracao-das/${clienteId}/${a.competencia}/html`, 'apuracao de DAS')
      .catch(err => setMensagem({ tipo: 'danger', txt: 'Não foi possível gerar: ' + err.message }));

  return (
    <>
      <PageHeader
        title="Apuração DAS — Simples Nacional"
        sub="Anexos III/IV/V (LC 123/2006) · Fator R puxado do Átrio Notas"
        actions={
          <button className="primary" onClick={() => setFormAberto(v => !v)}>
            {formAberto ? '▴ Ocultar formulário' : '+ Nova apuração'}
          </button>
        }
      />

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      <div className="kpi-grid">
        <KPI label="Total apurado no ano" value={fmtMoeda(totalAno)} />
        <KPI label="Aprovadas" value={aprovadas} variant={aprovadas > 0 ? 'success' : ''} />
        <KPI label="Rascunhos" value={rascunhos} variant={rascunhos > 0 ? 'warning' : ''} />
        {ultima && (
          <KPI
            label={`Última (${ultima.competencia})`}
            value={`Anexo ${ultima.anexo} · Faixa ${ultima.faixa}`}
            sub={`Alíq. efetiva ${(Number(ultima.aliquota_efetiva) * 100).toFixed(2)}%`}
          />
        )}
      </div>

      {formAberto && (
        <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '3px solid var(--gold)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
            Nova apuração
          </h3>
          <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label className="small muted">Competência</label>
              <input
                type="month"
                value={formComp}
                onChange={e => setFormComp(e.target.value)}
                style={{ width: 140 }}
              />
            </div>
            <div>
              <label className="small muted">Receita do mês (vazio = automático)</label>
              <input
                type="number" step="0.01" min="0"
                value={formReceitaMes}
                onChange={e => setFormReceitaMes(e.target.value)}
                placeholder="auto"
                style={{ width: 180, textAlign: 'right' }}
              />
            </div>
            <div>
              <label className="small muted">RBT12 (vazio = usa Notas)</label>
              <input
                type="number" step="0.01" min="0"
                value={formRbt12}
                onChange={e => setFormRbt12(e.target.value)}
                placeholder="auto"
                style={{ width: 160, textAlign: 'right' }}
              />
            </div>
            <div>
              <label className="small muted">Anexo</label>
              <select value={formAnexoForcado} onChange={e => setFormAnexoForcado(e.target.value)} style={{ width: 200 }}>
                <option value="auto">Auto (via Fator R)</option>
                <option value="III">III (forçado)</option>
                <option value="IV">IV (advocacia/construção)</option>
                <option value="V">V (forçado)</option>
              </select>
            </div>
            <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={formAtividadeIV}
                onChange={e => setFormAtividadeIV(e.target.checked)}
                style={{ width: 'auto' }}
              />
              CNAE Anexo IV
            </label>
            <button className="primary" onClick={apurar} disabled={apurando}>
              {apurando ? 'Apurando…' : 'Apurar'}
            </button>
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>
            Receita e RBT12 são resolvidos automaticamente (escriturado → NFS-e → Notas). Preencha manualmente apenas como último recurso. Fator R: ≥28% Anexo III, &lt;28% Anexo V; advocacia/construção usam Anexo IV (INSS separado).
          </p>
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading /> : apuracoes.length === 0 ? (
          <Empty title={`Sem apurações em ${filtroAno}`} sub="Clique em + Nova apuração pra começar." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Competência</th>
                <th>Anexo</th>
                <th>Faixa</th>
                <th className="num">RBT12</th>
                <th className="num">Receita mês</th>
                <th className="num">Aliq. efetiva</th>
                <th className="num">DAS R$</th>
                <th>Fator R</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {apuracoes.map(a => (
                <tr key={a.id}>
                  <td className="bold">{a.competencia}</td>
                  <td><span className="badge badge--info">Anexo {a.anexo}</span></td>
                  <td>{a.faixa}</td>
                  <td className="num">
                    {fmtMoeda(a.rbt12)}
                    {a.rbt12_origem && <div className="muted" style={{ fontSize: 9 }}>{a.rbt12_origem}</div>}
                  </td>
                  <td className="num">
                    {fmtMoeda(a.receita_mes)}
                    {a.receita_origem && <div className="muted" style={{ fontSize: 9 }}>{a.receita_origem}</div>}
                  </td>
                  <td className="num">{(Number(a.aliquota_efetiva) * 100).toFixed(2)}%</td>
                  <td className="num bold">{fmtMoeda(a.valor_das)}</td>
                  <td className="small mono">
                    {a.fator_r != null ? (
                      <>
                        {(Number(a.fator_r) * 100).toFixed(2)}%
                        <div className="muted" style={{ fontSize: 9 }}>{a.fator_r_origem}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="small">{fmtData(a.darf_data_vencto)}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td className="flex">
                    <button className="ghost" onClick={() => setDetalhe(a)}>Ver</button>
                    <button className="ghost" onClick={() => imprimir(a)}
                      title="Memoria de calculo em A4, com assinaturas">&#128424;</button>
                    {a.status === 'rascunho' && (
                      <button className="primary" onClick={() => aprovar(a.id)}>Aprovar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && <DetalheDAS apuracao={detalhe} onClose={() => setDetalhe(null)} />}
    </>
  );
}

// =============================================================================
// Modal de detalhe — repartição por tributo
// =============================================================================
function DetalheDAS({ apuracao, onClose }) {
  const repart = [
    { label: 'IRPJ',       valor: apuracao.valor_irpj },
    { label: 'CSLL',       valor: apuracao.valor_csll },
    { label: 'COFINS',     valor: apuracao.valor_cofins },
    { label: 'PIS/PASEP',  valor: apuracao.valor_pis },
    { label: 'CPP (INSS)', valor: apuracao.valor_cpp },
    { label: 'ISS',        valor: apuracao.valor_iss },
  ];
  const total = repart.reduce((s, r) => s + Number(r.valor || 0), 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: 640, maxHeight: '85vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>DAS {apuracao.competencia}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose}>Fechar</button>
        </div>

        <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
          <KPI label="Anexo" value={apuracao.anexo} />
          <KPI label="Faixa" value={apuracao.faixa} />
          <KPI label="DAS total" value={fmtMoeda(apuracao.valor_das)} />
        </div>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 16, marginBottom: 8 }}>
          Bases do cálculo
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <tbody>
            <tr><td>RBT12 (12 meses)</td><td className="num bold">{fmtMoeda(apuracao.rbt12)}</td></tr>
            <tr><td>Receita do mês</td><td className="num bold">{fmtMoeda(apuracao.receita_mes)}</td></tr>
            <tr><td>Alíquota nominal</td><td className="num">{(Number(apuracao.aliquota_nominal) * 100).toFixed(2)}%</td></tr>
            <tr><td>Parcela a deduzir</td><td className="num">{fmtMoeda(apuracao.parcela_deduzir)}</td></tr>
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">Alíquota efetiva</td>
              <td className="num bold" style={{ color: 'var(--gold)' }}>{(Number(apuracao.aliquota_efetiva) * 100).toFixed(4)}%</td>
            </tr>
            {apuracao.fator_r != null && (
              <tr>
                <td>Fator R · origem {apuracao.fator_r_origem}</td>
                <td className="num">{(Number(apuracao.fator_r) * 100).toFixed(2)}%</td>
              </tr>
            )}
          </tbody>
        </table>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 20, marginBottom: 8 }}>
          Repartição do DAS por tributo
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <thead>
            <tr><th>Tributo</th><th className="num">Valor R$</th><th className="num">% do DAS</th></tr>
          </thead>
          <tbody>
            {repart.map(r => {
              const pct = total > 0 ? (Number(r.valor || 0) / total * 100) : 0;
              return (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td className="num">{fmtMoeda(r.valor)}</td>
                  <td className="num small muted">{pct.toFixed(2)}%</td>
                </tr>
              );
            })}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">Total</td>
              <td className="num bold">{fmtMoeda(total)}</td>
              <td className="num bold">100%</td>
            </tr>
          </tbody>
        </table>

        {apuracao.sublimite_iss_atingido && (
          <Alert tipo="warning">
            ⚠ Sublimite ISS R$ 3,6M atingido — ISS é recolhido direto ao município, fora do DAS.
          </Alert>
        )}

        <div className="flex" style={{ marginTop: 20, gap: 12, justifyContent: 'space-between' }}>
          <div className="small muted">
            DARF código <strong>{apuracao.darf_codigo}</strong> · vencimento <strong>{fmtData(apuracao.darf_data_vencto)}</strong>
          </div>
          <button className="primary" disabled title="Geração de DARF — próxima sprint">
            Gerar DARF (em breve)
          </button>
        </div>
      </div>
    </div>
  );
}
